import React, { FC } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, Divider, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import logo from 'assets/dark-logo.png';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { helpDrawerVar } from 'apollo/reactive-variables/help-drawer';
import { getLatestPublishedReleaseNoteTemplate } from 'apollo/templates/release-notes';
import { ReleaseNote } from 'models/release-note';
import { resolveOrgType } from 'utils/release-note-utils';

type Props = {
    open: boolean;
    onClose: () => void;
};

const AboutDenthubDialog: FC<Props> = ({ open, onClose }) => {
    const { t: translate } = useTranslation(['navigation', 'common']);
    const navigate = useNavigate();
    const currentOrg = currentOrgVar();

    const { data } = useQuery<{ latestPublishedReleaseNote: ReleaseNote }>(getLatestPublishedReleaseNoteTemplate, {
        variables: { organizationType: resolveOrgType(currentOrg.type) },
        fetchPolicy: 'cache-and-network',
        skip: !open,
    });

    const appVersion = data?.latestPublishedReleaseNote?.version ?? process.env.REACT_APP_DEFAULT_VERSION ?? '1.0';

    const handleReleaseNotes = () => {
        onClose();
        navigate('/release-notes-history');
    };

    const handleHelpAndSupport = () => {
        onClose();
        helpDrawerVar(true);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, overflow: 'hidden' },
            }}
        >
            <IconButton
                onClick={onClose}
                size="small"
                aria-label={translate('common:CLOSE')}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    zIndex: 2,
                    color: 'rgba(8,19,46,0.55)',
                    '&:hover': { color: '#08132E', bgcolor: 'rgba(255,255,255,0.45)' },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box
                sx={{
                    position: 'relative',
                    pt: 5,
                    pb: 3.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    overflow: 'hidden',
                    background:
                        'linear-gradient(160deg, #FAD7CB 0%, #F6C6B8 25%, #F2B4A3 55%, #EDA292 80%, #E89283 100%)',
                }}
            >
                <Box
                    component="svg"
                    viewBox="0 0 400 200"
                    preserveAspectRatio="none"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '130%',
                        height: '120%',
                        left: '-15%',
                        top: '-5%',
                        pointerEvents: 'none',
                        transformOrigin: 'center',
                        animation: 'aboutDenthubBreathe 12s ease-in-out infinite alternate',
                        '@keyframes aboutDenthubBreathe': {
                            '0%': { transform: 'scale(1) translateY(0.8%)' },
                            '50%': { transform: 'scale(1.3) translateY(-0.8%)' },
                            '100%': { transform: 'scale(1.8) translateY(0.8%)' },
                        },
                    }}
                >
                    <defs>
                        <linearGradient
                            id="aboutDenthubSwirlStroke"
                            gradientUnits="userSpaceOnUse"
                            x1="0"
                            y1="0"
                            x2="700"
                            y2="0"
                        >
                            <stop offset="0%" stopColor="rgba(255,235,225,0.2)" />
                            <stop offset="25%" stopColor="rgba(255,225,210,0.55)" />
                            <stop offset="50%" stopColor="rgba(210,180,220,0.3)" />
                            <stop offset="72%" stopColor="rgba(252,76,52,0.75)" />
                            <stop offset="100%" stopColor="rgba(252,76,52,0.1)" />
                        </linearGradient>
                        <filter id="aboutDenthubSwirlBlur" x="-20%" y="-20%" width="120%" height="120%">
                            <feGaussianBlur stdDeviation="1.4" />
                        </filter>
                    </defs>
                    <g filter="url(#aboutDenthubSwirlBlur)">
                        <animateTransform
                            attributeName="transform"
                            type="translate"
                            values="0,0; 500,0; 0,0"
                            keyTimes="0; 0.5; 1"
                            calcMode="spline"
                            keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                            dur="120s"
                            repeatCount="indefinite"
                        />
                        {[
                            { baseY: 100, amplitude: 82, width: 48, opacity: 0.85 },
                            { baseY: 70, amplitude: 54, width: 68, opacity: 0.55 },
                            { baseY: 130, amplitude: 70, width: 30, opacity: 0.45 },
                            { baseY: 45, amplitude: 64, width: 24, opacity: 0.3 },
                            { baseY: 158, amplitude: 58, width: 18, opacity: 0.25 },
                            { baseY: 22, amplitude: 56, width: 14, opacity: 0.22 },
                            { baseY: 180, amplitude: 50, width: 10, opacity: 0.2 },
                            { baseY: 10, amplitude: 34, width: 14, opacity: 0.18 },
                            { baseY: 195, amplitude: 46, width: 16, opacity: 0.17 },
                        ].map((curve, lineIndex) => {
                            const period = 700;
                            const halfP = period / 2;
                            const ctrlOffset = halfP * 0.36;
                            const phaseOffset = (lineIndex * period) / 9;
                            const startX = -1400 + phaseOffset;
                            const numHalfWaves = 12;
                            let d = `M ${startX},${curve.baseY}`;
                            for (let segIndex = 0; segIndex < numHalfWaves; segIndex++) {
                                const segStartX = startX + segIndex * halfP;
                                const segEndX = segStartX + halfP;
                                const sign = segIndex % 2 === 0 ? -1 : 1;
                                const peakY = curve.baseY + sign * curve.amplitude * 1.2;
                                const c1x = segStartX + ctrlOffset;
                                const c2x = segEndX - ctrlOffset;
                                d += ` C ${c1x},${peakY} ${c2x},${peakY} ${segEndX},${curve.baseY}`;
                            }
                            return (
                                <path
                                    key={lineIndex}
                                    d={d}
                                    fill="none"
                                    stroke="url(#aboutDenthubSwirlStroke)"
                                    strokeWidth={curve.width}
                                    strokeLinecap="round"
                                    opacity={curve.opacity}
                                    shapeRendering="geometricPrecision"
                                />
                            );
                        })}
                    </g>
                </Box>

                <img
                    src={logo}
                    alt="DentHub"
                    style={{ height: 50, objectFit: 'contain', position: 'relative', zIndex: 1 }}
                />

                <Chip
                    label={`v${appVersion}`}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: '#08132E',
                        fontWeight: 600,
                        border: '1px solid rgba(8,19,46,0.12)',
                        backdropFilter: 'blur(1px)',
                    }}
                />
            </Box>
            <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2.5 }}>
                <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    <Button size="small" startIcon={<ArticleOutlinedIcon />} onClick={handleReleaseNotes}>
                        {translate('navigation:RELEASE_NOTES')}
                    </Button>
                    <Button size="small" startIcon={<HelpOutlineIcon />} onClick={handleHelpAndSupport}>
                        {translate('navigation:HELP_AND_SUPPORT')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AboutDenthubDialog;
